import { List } from '@chakra-ui/react';
import { IUsageEventRequestSchema } from '@monetize/types/app';
import { MimeTypeEnum } from '@monetize/types/common';
import { getFormattedDateUtc, parseDatetime } from '@monetize/utils/core';
import toNumber from 'lodash/toNumber';
import { parse, unparse } from 'papaparse';
import { FunctionComponent as FC, useState } from 'react';
import { FiDownload } from 'react-icons/fi';
import { v4 as uuidv4 } from 'uuid';
import {
  MAlert,
  MBox,
  MButton,
  MFlex,
  MIcon,
  MPageLoader,
  MText,
} from '../../../../../components/Monetize';
import { MFileDragDropUpload } from '../../../../../components/Monetize/MFileDragDropUpload';
import { ISO8601Format } from '../../../../../constants/dates';
import { useUsageEventCustomFields } from '../../../../../hooks/useUsageEventCustomFields';
import { useFlags } from '../../../../../services/launchDarkly';
import { logger } from '../../../../../services/logger';
import { saveFile } from '../../../../../utils/download';
import {
  getUsageTemplateFields,
  processUsageCsvParse,
} from './processUsageCsvParse';

const MAX_ERROR_DISPLAY = 10;

interface UploadUsageInputProps {
  recordData: {
    accountId?: string;
    subscriptionId?: string;
  };
  onData: (
    filename: string,
    data: IUsageEventRequestSchema[],
    warnings: string[],
  ) => void;
}

/**
 * Allow user to upload a usage file and have the results processed and returned
 */
export const UploadUsageInput: FC<UploadUsageInputProps> = ({
  recordData,
  onData,
}: UploadUsageInputProps) => {
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const { enableUsageCustomFields } = useFlags();
  const {
    configuredKeys,
    customFields,
    isLoading: isLoadingCustomFields,
  } = useUsageEventCustomFields();

  const handleDownloadTemplate = () => {
    const { accountId, subscriptionId } = recordData;
    const EXAMPLE_RECORD = {
      id: accountId ? uuidv4().slice(0, 23) : '',
      accountId,
      subscriptionId,
      unitsConsumed: '1',
      timestamp: getFormattedDateUtc(new Date(), ISO8601Format),
      usageTypeId: '',
    };
    const csv = unparse(
      {
        fields: getUsageTemplateFields(
          configuredKeys,
          !!enableUsageCustomFields,
        ),
        data: [EXAMPLE_RECORD],
      },
      { header: true },
    );
    saveFile(csv, `usage-template.csv`, MimeTypeEnum.CSV);
  };

  const handleFileUpload = (file: File) => {
    setFileErrors([]);
    parse<Partial<IUsageEventRequestSchema>>(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.replace(/^\uFEFF/, '').trim(),
      transform: (value, header: string) => {
        if (header === 'unitsConsumed') {
          const numberValue = Number(value || 0) as number;
          return isNaN(numberValue) ? 0 : numberValue;
        } else if (header === 'timestamp') {
          return parseDatetime(value);
        } else if (header === 'ratedPrice') {
          return value ? toNumber(value) : null;
        }
        return value;
      },
      complete: ({ data, errors, meta }) => {
        try {
          if (errors?.length) {
            setFileErrors(
              errors.map(
                ({ message, row }) => `${row ? `Row ${row}: ` : ''}${message}`,
              ),
            );
            logger.warn('Error parsing usage file', errors);
            return;
          }

          const actualFields = (meta.fields ?? []) as string[];
          const parseResult = processUsageCsvParse({
            rows: data,
            fields: actualFields,
            recordData,
            enableUsageCustomFields,
            configuredPropertyKeys: configuredKeys,
          });

          if (parseResult.status === 'error') {
            setFileErrors(parseResult.errors);
            logger.warn('Error parsing usage file', parseResult.errors);
            return;
          }

          logger.log('Parsed file', { results: parseResult.results });
          onData(file.name, parseResult.results, parseResult.warnings);
        } catch (ex) {
          logger.warn('Error parsing usage file', ex);
          logger.log({ data, errors });
          setFileErrors([
            ex instanceof Error
              ? `Error parsing file: ${ex.message}`
              : 'There was an error parsing the file. Please try again.',
          ]);
        }
      },
    });
  };

  return (
    <MFlex flexDirection="column">
      {fileErrors.length > 0 && (
        <MAlert type="error">
          There was an error with the file you are trying to import. Correct
          your file and try again.
          <List.Root as="ul" mt={2}>
            {fileErrors.slice(0, MAX_ERROR_DISPLAY).map((error) => (
              <li key={error}>{error}</li>
            ))}
          </List.Root>
          {fileErrors.length > MAX_ERROR_DISPLAY && (
            <MBox mt={2}>
              And {fileErrors.length - MAX_ERROR_DISPLAY} more errors...
            </MBox>
          )}
        </MAlert>
      )}
      {isLoadingCustomFields ? (
        // The template header and upload validation both depend on the
        // configured custom fields, so hold the actions until they arrive.
        <MPageLoader height="auto" py={12} data-testid="usage-upload-loading" />
      ) : (
        <>
          <MFileDragDropUpload
            p={5}
            mt={4}
            accept={['csv']}
            label="Drag and Drop your file here"
            subtitle="You can download our usage template to get started."
            onFileUpload={handleFileUpload}
            maxFileSize={10}
          />
          <MBox>
            <MButton
              variant="tertiary"
              size="sm"
              ml={-3}
              mt={4}
              onClick={handleDownloadTemplate}
            >
              <MIcon as={FiDownload} mr={2} />
              Download Template
            </MButton>
          </MBox>
        </>
      )}
      <MBox>
        <MText mt={4} fontWeight="600">
          The uploaded CSV should contain the following columns:
        </MText>
        <List.Root as="ul">
          <List.Item>
            <MText>
              <strong>id:</strong> (Optional) Unique ID for this record, this
              will be auto-generated if this column is left blank
            </MText>
          </List.Item>
          <List.Item>
            <MText>
              <strong>timestamp:</strong> The date and time when the usage event
              occurred. This must use ISO8601 UTC format. Billing always runs in
              UTC.
            </MText>
          </List.Item>
          <List.Item>
            <MText>
              <strong>accountId:</strong> The ID of the account associated with
              this usage event
              {recordData.accountId
                ? ' (Optional — already provided by context)'
                : ''}
            </MText>
          </List.Item>
          <List.Item>
            <MText>
              <strong>subscriptionId:</strong> The ID of the subscription
              associated with this usage event
              {recordData.subscriptionId
                ? ' (Optional — already provided by context)'
                : ''}
            </MText>
          </List.Item>
          <List.Item>
            <MText>
              <strong>usageTypeId:</strong> The ID of the usage type associated
              with this usage event
            </MText>
          </List.Item>
          <List.Item>
            <MText>
              <strong>unitsConsumed:</strong> The number of units consumed in
              this usage event
            </MText>
          </List.Item>
          <List.Item>
            <MText>
              <strong>ratedPrice:</strong> (Optional) Price override for this
              usage record. If provided, this will override the calculated price
              based on the usage type's rate card.
            </MText>
          </List.Item>
          {enableUsageCustomFields && (
            <List.Item>
              <MText>
                <strong>properties.&lt;name&gt;:</strong> (Optional) One column
                per usage event custom field, using the field's API name with
                the prefix <strong>properties.</strong>
                {customFields.length > 0 ? (
                  <>
                    {' '}
                    (e.g. <strong>properties.{customFields[0].key}</strong>).
                    The template includes a column for every configured field.
                  </>
                ) : (
                  <>
                    . Configure fields under Settings &gt; Custom Fields (object
                    &quot;Usage Event&quot;).
                  </>
                )}{' '}
                Properties that are not configured are dropped by the server.
                Empty cells are skipped.
              </MText>
            </List.Item>
          )}
        </List.Root>
      </MBox>
    </MFlex>
  );
};
